
import { plotcardEntityKeys } from '@/services/query/plotcard-entities-queries';
import { secureTimelineKeys } from '@/services/query/secure-timeline-queries';
import { timelineKeys } from '@/services/query/timeline-queries';
import { Chapter, createChapter, deleteChapter, deleteChapters, getChaptersByProjectId, getChaptersWithStats, updateChapterTitle } from '@/services/supabase/chapters';
import { supabase } from '@/services/supabase/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Define cache times
const HOUR_IN_MS = 1000 * 60 * 60;
const DAY_IN_MS = HOUR_IN_MS * 24;
const STALE_TIME = DAY_IN_MS * 30; // 30 days stale time
const GC_TIME = DAY_IN_MS * 60; // 60 days garbage collection

export const chapterKeys = {
    all: ['content', 'chapters'] as const,
    list: (storyId: string) => [...chapterKeys.all, 'list', storyId] as const,
    projectList: (projectId: string) => [...chapterKeys.all, 'project-list', projectId] as const,
    detail: (chapterId: string | number) => [...chapterKeys.all, 'detail', chapterId.toString()] as const,
};

/**
 * Hook to fetch chapters for a story
 */
export function useChapters(
    storyId: string | undefined | null,
    options: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: chapterKeys.list(storyId || ''),
        queryFn: async () => {
            if (!storyId) return [];

            console.log(`Fetching chapters for story ${storyId}`);

            // Fetch Chapters with Stats using RPC
            return await getChaptersWithStats(parseInt(storyId));
        },
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        enabled: !!storyId && (options.enabled !== false),
    });
}

/**
 * Hook to fetch a single chapter
 */
export function useChapter(
    chapterId: string | number | undefined,
    options: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: chapterKeys.detail(chapterId || ''),
        queryFn: async () => {
            if (!chapterId) return null;
            console.log(`Fetching chapter ${chapterId}`);

            const { data, error } = await supabase
                .from('chapters')
                .select('*')
                .eq('id', chapterId)
                .single();

            if (error) {
                console.error('Error fetching chapter:', error);
                throw error;
            }

            return data as Chapter;
        },
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        enabled: !!chapterId && (options.enabled !== false),
    });
}

/**
 * Hook to fetch all chapters for a project (across all stories)
 */
export function useChaptersByProject(
    projectId: string | undefined | null,
    options: { enabled?: boolean } = {}
) {
    return useQuery({
        queryKey: chapterKeys.projectList(projectId || ''),
        queryFn: async () => {
            if (!projectId) return [];

            console.log(`Fetching all chapters for project ${projectId}`);
            return await getChaptersByProjectId(parseInt(projectId));
        },
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        enabled: !!projectId && (options.enabled !== false),
    });
}

/**
 * Hook to create a new chapter
 */
export function useCreateChapter() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            storyId,
            title,
            description,
            projectId
        }: {
            storyId: number;
            title: string;
            description?: string;
            projectId: string;
        }) => {
            console.log(`Creating chapter for story ${storyId} with title: ${title}`);
            return await createChapter(storyId, title, description);
        },
        onSuccess: (newChapter, variables) => {
            const storyIdStr = variables.storyId.toString();

            // Invalidate content chapters list
            queryClient.invalidateQueries({
                queryKey: chapterKeys.list(storyIdStr)
            });

            // Invalidate timeline chapters list (used by timeline) - kept for compatibility
            queryClient.invalidateQueries({
                queryKey: timelineKeys.chapters(storyIdStr)
            });

            // Invalidate secure timeline data (used by timeline)
            queryClient.invalidateQueries({
                queryKey: secureTimelineKeys.timeline(variables.projectId)
            });

            // Invalidate raw timeline data
            queryClient.invalidateQueries({
                queryKey: secureTimelineKeys.raw(variables.projectId)
            });
        },
    });
}

/**
 * Hook to update a chapter's title
 */
export function useUpdateChapter() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            chapterId,
            title,
            description,
            projectId
        }: {
            chapterId: number;
            title: string;
            description?: string;
            projectId: string;
        }) => {
            console.log(`Updating chapter ${chapterId} with title: ${title}, description: ${description}`);
            return await updateChapterTitle(chapterId, title, description);
        },
        onSuccess: (updatedChapter, variables) => {
            // Update the chapter in the cache
            queryClient.setQueryData(
                chapterKeys.detail(variables.chapterId),
                updatedChapter
            );

            if (updatedChapter.story_id) {
                const storyIdStr = updatedChapter.story_id.toString();

                // Invalidate content chapters list
                queryClient.invalidateQueries({
                    queryKey: chapterKeys.list(storyIdStr)
                });

                // Invalidate timeline chapters list - kept for compatibility
                queryClient.invalidateQueries({
                    queryKey: timelineKeys.chapters(storyIdStr)
                });

                // Invalidate secure timeline data
                queryClient.invalidateQueries({
                    queryKey: secureTimelineKeys.timeline(variables.projectId)
                });

                // Invalidate raw timeline data
                queryClient.invalidateQueries({
                    queryKey: secureTimelineKeys.raw(variables.projectId)
                });
            }
        },
    });
}

/**
 * Hook to delete a chapter
 */
export function useDeleteChapter() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            chapterId,
            storyId,
            projectId
        }: {
            chapterId: number;
            storyId: string;
            projectId: string;
        }) => {
            console.log(`Deleting chapter ${chapterId}`);
            await deleteChapter(chapterId);
            return { chapterId, storyId, projectId };
        },
        onSuccess: (result) => {
            // Invalidate content chapters list
            queryClient.invalidateQueries({
                queryKey: chapterKeys.list(result.storyId)
            });

            // Invalidate timeline chapters list - kept for compatibility
            queryClient.invalidateQueries({
                queryKey: timelineKeys.chapters(result.storyId)
            });

            // Invalidate secure timeline data
            queryClient.invalidateQueries({
                queryKey: secureTimelineKeys.timeline(result.projectId)
            });

            // Invalidate raw timeline data
            queryClient.invalidateQueries({
                queryKey: secureTimelineKeys.raw(result.projectId)
            });

            // Remove chapter-specific caches
            queryClient.removeQueries({
                queryKey: chapterKeys.detail(result.chapterId)
            });

            // Remove chapter entities cache
            queryClient.removeQueries({
                queryKey: plotcardEntityKeys.byChapter(result.chapterId)
            });

            // Remove chapter plot cards cache
            queryClient.removeQueries({
                queryKey: timelineKeys.chapterCards(result.chapterId)
            });
        },
    });
}

/**
 * Hook to delete multiple chapters
 */
export function useDeleteChapters() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            chapterIds,
            storyId,
            projectId
        }: {
            chapterIds: number[];
            storyId: string;
            projectId: string;
        }) => {
            console.log(`Deleting chapters: ${chapterIds.join(', ')}`);
            await deleteChapters(chapterIds);
            return { chapterIds, storyId, projectId };
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: chapterKeys.list(result.storyId) });
            queryClient.invalidateQueries({ queryKey: timelineKeys.chapters(result.storyId) });
            queryClient.invalidateQueries({ queryKey: secureTimelineKeys.timeline(result.projectId) });
            queryClient.invalidateQueries({ queryKey: secureTimelineKeys.raw(result.projectId) });

            // Remove specific caches for each deleted chapter
            result.chapterIds.forEach(id => {
                queryClient.removeQueries({ queryKey: chapterKeys.detail(id) });
                queryClient.removeQueries({ queryKey: plotcardEntityKeys.byChapter(id) });
                queryClient.removeQueries({ queryKey: timelineKeys.chapterCards(id) });
            });
        },
    });
}
